<form [formGroup]="form" (submit)="submit()">
  <app-dialog-layout
    [title]="'Change Password' | translate"
    [showCloseButton]="!data?.requireNewPassword"
    [showCancelButton]="!data?.requireNewPassword"
    [primaryButtonLabel]="'Change Password' | translate"
    [disablePrimaryButton]="form.invalid || submitting"
    [loading]="submitting"
  >
    <div class="space-y-4 py-3">
      <div class="grid grid-cols-1 md:grid-cols-12 gap-6">
        <!-- Form fields side -->
        <div class="md:col-span-7 space-y-4">
          <!-- Current Password -->
          <mat-form-field appearance="outline" class="animate-fade-up w-full custom-input">
            <mat-label>{{ 'Current Password' | translate }}</mat-label>
            <input
              matInput
              [type]="showCurrentPassword ? 'text' : 'password'"
              formControlName="currentPassword"
            />
            <button
              mat-icon-button
              matSuffix
              type="button"
              (click)="toggleShowCurrentPassword()"
              [attr.aria-label]="showCurrentPassword ? 'Hide password' : 'Show password'"
            >
              <mat-icon>{{ showCurrentPassword ? 'visibility_off' : 'visibility' }}</mat-icon>
            </button>
            @if (form.get('currentPassword')?.touched && form.get('currentPassword')?.hasError('required')) {
              <mat-error>{{ 'Current password is required' | translate }}</mat-error>
            }
          </mat-form-field>

          <!-- New Password -->
          <mat-form-field appearance="outline" class="animate-fade-up w-full custom-input">
            <mat-label>{{ 'New Password' | translate }}</mat-label>
            <input
              matInput
              [type]="showNewPassword ? 'text' : 'password'"
              formControlName="newPassword"
              (input)="checkStrength($event.target.value)"
            />
            <button
              mat-icon-button
              matSuffix
              type="button"
              (click)="toggleShowNewPassword()"
              [attr.aria-label]="showNewPassword ? 'Hide password' : 'Show password'"
            >
              <mat-icon>{{ showNewPassword ? 'visibility_off' : 'visibility' }}</mat-icon>
            </button>
            @if (form.get('newPassword')?.touched && form.get('newPassword')?.hasError('required')) {
              <mat-error>{{ 'New password is required' | translate }}</mat-error>
            }
          </mat-form-field>

          <!-- Confirm Password -->
          <mat-form-field appearance="outline" class="animate-fade-up w-full custom-input">
            <mat-label>{{ 'Confirm New Password' | translate }}</mat-label>
            <input
              matInput
              [type]="showConfirmPassword ? 'text' : 'password'"
              formControlName="confirmPassword"
            />
            <button
              mat-icon-button
              matSuffix
              type="button"
              (click)="toggleShowConfirmPassword()"
              [attr.aria-label]="showConfirmPassword ? 'Hide password' : 'Show password'"
            >
              <mat-icon>{{ showConfirmPassword ? 'visibility_off' : 'visibility' }}</mat-icon>
            </button>
            @if (form.get('confirmPassword')?.touched && form.get('confirmPassword')?.hasError('required')) {
              <mat-error>{{ 'Confirm password is required' | translate }}</mat-error>
            }
            @if (form.get('confirmPassword')?.touched && form.get('confirmPassword')?.hasError('mismatch')) {
              <mat-error>{{ 'Passwords do not match' | translate }}</mat-error>
            }
          </mat-form-field>
        </div>

        <!-- Security Checklist side -->
        <div class="md:col-span-5">
          <div class="rounded-[24px] border border-slate-200 dark:border-slate-800 border-l-4 border-l-amber-400 bg-sky-50 dark:bg-slate-900/50 p-5 text-slate-700 dark:text-slate-300 shadow-sm">
            <div class="space-y-4">
              <div class="space-y-1">
                <p class="text-[10px] font-semibold uppercase tracking-[0.24em] text-primary dark:text-primary-container">
                  {{ 'Strength Protocol' | translate }}
                </p>
                <h3 class="text-lg font-semibold leading-tight text-slate-900 dark:text-white">{{ 'Password security checklist' | translate }}</h3>
                <p class="text-xs leading-normal text-slate-500 dark:text-slate-400">
                  {{ 'Your password should be memorable to you and difficult for others to guess.' | translate }}
                </p>
              </div>

              @if (form.controls.newPassword.dirty || form.controls.newPassword.touched) {
                <div class="flex flex-col">
                  <span class="text-xs font-medium text-slate-700 dark:text-slate-300">{{ 'Password Strength' | translate }}</span>
                  <div class="mt-1.5 mb-3 h-2 w-full overflow-hidden rounded-full bg-slate-200">
                    <div
                      class="h-2 rounded-full transition-all duration-300"
                      [ngClass]="
                        passwordStrength === 100
                          ? 'bg-green-500'
                          : passwordStrength >= 75
                            ? 'bg-yellow-400'
                            : passwordStrength >= 50
                              ? 'bg-orange-400'
                              : 'bg-red-500'
                      "
                      [style.width.%]="passwordStrength"
                    ></div>
                  </div>

                  <ul class="space-y-2 text-xs">
                    @let strength = form.controls.newPassword.errors?.['passwordStrength'];

                    <li>
                      <div class="flex items-center gap-2">
                        @if (strength?.minLength ?? true) {
                          <lucide-icon [img]="circleCheck" size="14" class="text-green-500" />
                        } @else {
                          <lucide-icon [img]="circleX" size="14" class="text-red-500" />
                        }
                        <span>{{ 'Minimum 8 characters' | translate }}</span>
                      </div>
                    </li>

                    <li>
                      <div class="flex items-center gap-2">
                        @if (strength?.hasUpperCase ?? true) {
                          <lucide-icon [img]="circleCheck" size="14" class="text-green-500" />
                        } @else {
                          <lucide-icon [img]="circleX" size="14" class="text-red-500" />
                        }
                        <span>{{ 'Must contain uppercase letter' | translate }}</span>
                      </div>
                    </li>

                    <li>
                      <div class="flex items-center gap-2">
                        @if (strength?.hasLowerCase ?? true) {
                          <lucide-icon [img]="circleCheck" size="14" class="text-green-500" />
                        } @else {
                          <lucide-icon [img]="circleX" size="14" class="text-red-500" />
                        }
                        <span>{{ 'Must contain lowercase letter' | translate }}</span>
                      </div>
                    </li>

                    <li>
                      <div class="flex items-center gap-2">
                        @if (strength?.hasNumber ?? true) {
                          <lucide-icon [img]="circleCheck" size="14" class="text-green-500" />
                        } @else {
                          <lucide-icon [img]="circleX" size="14" class="text-red-500" />
                        }
                        <span>{{ 'Must contain a number' | translate }}</span>
                      </div>
                    </li>

                    <li>
                      <div class="flex items-center gap-2">
                        @if (strength?.hasSpecialChar ?? true) {
                          <lucide-icon [img]="circleCheck" size="14" class="text-green-500" />
                        } @else {
                          <lucide-icon [img]="circleX" size="14" class="text-red-500" />
                        }
                        <span>{{ 'Must contain special character' | translate }}</span>
                      </div>
                    </li>
                  </ul>
                </div>
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  </app-dialog-layout>
</form>
